import React, { useState, useEffect, useCallback } from 'react';
import { Box, styled, GlobalStyles } from '@mui/material';

// Types
interface ImageItem {
  src: string;
  caption?: string;
}

interface ModalState {
  isOpen: boolean;
  images: ImageItem[];
  currentIndex: number;
}

// Styled Components
const globalStyles = (
  <GlobalStyles
    styles={{
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0,
      },
      '[data-theme="dark"]': {
        '--primary-color': '#9ca3af',
        '--primary-dark': '#d1d5db',
        '--bg-color': '#111827',
        '--card-bg': '#1f2937',
        '--text-color': '#f9fafb',
        '--text-muted': '#9ca3af',
        '--border-color': '#374151',
        '--accent-color': '#6b7280',
        '--code-bg': '#374151',
        '--callout-bg': '#374151',
      },
    }}
  />
);

const ManualContainer = styled(Box)(({ theme }) => ({
  '--primary-color': '#374151',
  '--primary-dark': '#1f2937',
  '--bg-color': '#f9fafb',
  '--card-bg': '#ffffff',
  '--text-color': '#111827',
  '--text-muted': '#6b7280',
  '--border-color': '#e5e7eb',
  '--accent-color': '#4b5563',
  '--sidebar-width': '280px',
  '--code-bg': '#f3f4f6',
  '--callout-bg': '#f3f4f6',
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  background: 'var(--bg-color)',
  color: 'var(--text-color)',
  lineHeight: 1.6,
  minHeight: '100vh',
  '&[data-theme="dark"]': {
    '--primary-color': '#9ca3af',
    '--primary-dark': '#d1d5db',
    '--bg-color': '#111827',
    '--card-bg': '#1f2937',
    '--text-color': '#f9fafb',
    '--text-muted': '#9ca3af',
    '--border-color': '#374151',
    '--accent-color': '#6b7280',
    '--code-bg': '#374151',
    '--callout-bg': '#374151',
  },
}));

const Sidebar = styled('nav')<{ isActive?: boolean }>(({ isActive }) => ({
  position: 'fixed',
  left: 0,
  top: 0,
  width: 'var(--sidebar-width)',
  height: '100vh',
  background: 'var(--card-bg)',
  borderRight: '1px solid var(--border-color)',
  overflowY: 'auto',
  zIndex: 100,
  padding: '24px 0',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease',
  '@media (max-width: 900px)': {
    transform: isActive ? 'translateX(0)' : 'translateX(-100%)',
    zIndex: 1000,
  },
}));

const SidebarHeader = styled('div')({
  padding: '0 24px 24px',
  borderBottom: '1px solid var(--border-color)',
  '& h1': {
    fontSize: '1.25rem',
    color: 'var(--primary-color)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});

const SidebarNav = styled('div')({
  padding: '16px 0',
  flex: 1,
});

const SidebarFooter = styled('div')({
  padding: '16px 24px',
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  borderTop: '1px solid var(--border-color)',
});

const NavSection = styled('div')({
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  padding: '16px 24px 8px',
  fontWeight: 600,
});

const NavItem = styled('a')<{ isActive?: boolean; isSub?: boolean }>(
  ({ isActive, isSub }) => ({
    display: 'block',
    padding: isSub ? '10px 24px 10px 40px' : '10px 24px',
    color: isActive ? 'var(--primary-color)' : 'var(--text-muted)',
    textDecoration: 'none',
    fontSize: isSub ? '0.85rem' : '0.9rem',
    borderLeft: `3px solid ${
      isActive ? 'var(--primary-color)' : 'transparent'
    }`,
    background: isActive ? 'rgba(55, 65, 81, 0.05)' : 'transparent',
    fontWeight: isActive ? 500 : 400,
    opacity: isSub ? 0.85 : 1,
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--primary-color)',
      borderLeftColor: 'var(--primary-color)',
      opacity: 1,
    },
  }),
);

const MobileNavToggle = styled('button')({
  display: 'none',
  background: 'transparent',
  color: 'var(--primary-color)',
  border: 'none',
  padding: '8px',
  borderRadius: '8px',
  cursor: 'pointer',
  '@media (max-width: 900px)': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '&:hover': {
    background: 'var(--code-bg)',
  },
  '& svg': {
    width: '24px',
    height: '24px',
    fill: 'var(--primary-color)',
  },
});

const MobileHeader = styled('div')({
  display: 'none',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  background: 'var(--card-bg)',
  borderBottom: '1px solid var(--border-color)',
  padding: '12px 16px',
  zIndex: 99,
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  '@media (max-width: 900px)': {
    display: 'flex',
  },
  '& h1': {
    fontSize: '1.1rem',
    color: 'var(--primary-color)',
    margin: 0,
    flex: 1,
    textAlign: 'center',
  },
});

const MainContent = styled('main')({
  marginLeft: 'var(--sidebar-width)',
  padding: '40px',
  maxWidth: '1000px',
  '@media (max-width: 900px)': {
    marginLeft: 0,
    padding: '80px 30px 40px',
  },
  '@media (max-width: 768px)': {
    padding: '70px 15px 30px',
  },
});

const Section = styled('section')({
  background: 'var(--card-bg)',
  borderRadius: '12px',
  padding: '32px',
  marginBottom: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  '@media (max-width: 768px)': {
    padding: '20px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  '& h2': {
    fontSize: '1.5rem',
    color: 'var(--text-color)',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid var(--primary-color)',
    '@media (max-width: 768px)': {
      fontSize: '1.3rem',
    },
  },
  '& h3': {
    fontSize: '1.1rem',
    color: 'var(--text-color)',
    margin: '24px 0 12px',
    '@media (max-width: 768px)': {
      fontSize: '1rem',
    },
  },
  '& h4': {
    fontSize: '0.9rem',
    color: 'var(--text-muted)',
    margin: '20px 0 8px',
    fontWeight: 600,
  },
  '& p': {
    marginBottom: '12px',
  },
});

const Callout = styled('div')<{ variant?: 'tip' | 'warning' | 'success' }>(
  ({ variant = 'tip' }) => ({
    padding: '16px 20px',
    borderRadius: '8px',
    margin: '16px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'var(--callout-bg)',
    borderLeft: `4px solid ${
      variant === 'warning'
        ? '#9ca3af'
        : variant === 'success'
        ? 'var(--accent-color)'
        : 'var(--primary-color)'
    }`,
    '@media (max-width: 768px)': {
      padding: '12px',
      fontSize: '0.9rem',
    },
  }),
);

const CalloutIcon = styled('span')({
  fontSize: '1.5rem',
  flexShrink: 0,
  '@media (max-width: 768px)': {
    fontSize: '1.2rem',
  },
});

const Code = styled('code')({
  background: 'var(--code-bg)',
  padding: '2px 8px',
  borderRadius: '4px',
  fontFamily: "'SF Mono', Monaco, Consolas, monospace",
  fontSize: '0.9em',
  color: 'var(--text-color)',
  '@media (max-width: 768px)': {
    fontSize: '0.85em',
    padding: '2px 6px',
  },
});

const Flow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  padding: '16px',
  background: 'var(--code-bg)',
  borderRadius: '8px',
  margin: '16px 0',
  '@media (max-width: 900px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

const FlowItem = styled('div')({
  background: 'var(--card-bg)',
  padding: '8px 16px',
  borderRadius: '20px',
  fontWeight: 500,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  color: 'var(--text-color)',
  '@media (max-width: 480px)': {
    width: '100%',
    textAlign: 'center',
  },
});

const FlowArrow = styled('span')({
  color: 'var(--primary-color)',
  fontSize: '1.5rem',
  '@media (max-width: 900px)': {
    transform: 'rotate(90deg)',
    alignSelf: 'center',
  },
});

const ImageGallery = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '16px',
  margin: '16px 0',
  '@media (max-width: 768px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  '@media (max-width: 480px)': {
    gridTemplateColumns: '1fr',
  },
});

const ImageItemWrapper = styled('div')({
  position: 'relative',
  cursor: 'pointer',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  background: 'var(--code-bg)',
  '&:hover': {
    opacity: 0.9,
  },
  '& img': {
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
  },
});

const ImageCaption = styled('div')({
  padding: '8px 12px',
  fontSize: '0.8rem',
  color: 'var(--text-color)',
  textAlign: 'center',
  background: 'var(--card-bg)',
});

const ZoomHint = styled('span')({
  position: 'absolute',
  top: '8px',
  right: '8px',
  background: 'rgba(0,0,0,0.6)',
  color: 'white',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '0.7rem',
  opacity: 0,
  '.image-item:hover &': {
    opacity: 1,
  },
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const Modal = styled('div')<{ isOpen: boolean }>(({ isOpen }) => ({
  display: isOpen ? 'flex' : 'none',
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,0.9)',
  zIndex: 1000,
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
}));

const ModalContent = styled('div')({
  maxWidth: '90%',
  maxHeight: '90%',
  position: 'relative',
  '& img': {
    maxWidth: '100%',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: '8px',
  },
});

const ModalClose = styled('button')({
  position: 'absolute',
  top: '8px',
  right: '8px',
  background: 'rgba(0,0,0,0.6)',
  border: 'none',
  color: 'white',
  fontSize: '1.5rem',
  cursor: 'pointer',
  padding: '6px 10px',
  borderRadius: '50%',
  lineHeight: 1,
  zIndex: 10,
  '&:hover': {
    background: 'rgba(0,0,0,0.8)',
  },
  '@media (max-width: 768px)': {
    top: '4px',
    right: '4px',
    fontSize: '1.3rem',
    padding: '4px 8px',
  },
});

const ModalCaption = styled('div')({
  color: 'white',
  textAlign: 'center',
  marginTop: '12px',
  fontSize: '0.9rem',
});

const ModalNav = styled('button')<{ direction: 'prev' | 'next' }>(
  ({ direction }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: 'white',
    fontSize: '2rem',
    padding: '16px',
    cursor: 'pointer',
    borderRadius: '4px',
    left: direction === 'prev' ? '20px' : 'auto',
    right: direction === 'next' ? '20px' : 'auto',
    '&:hover': {
      background: 'rgba(255,255,255,0.3)',
    },
    '@media (max-width: 768px)': {
      padding: '12px',
      fontSize: '1.5rem',
    },
  }),
);

const Table = styled('table')({
  width: '100%',
  borderCollapse: 'collapse',
  margin: '16px 0',
  '& th, & td': {
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid var(--border-color)',
    '@media (max-width: 768px)': {
      padding: '8px 10px',
      fontSize: '0.8rem',
    },
  },
  '& th': {
    background: 'var(--bg-color)',
    fontWeight: 600,
    color: 'var(--text-muted)',
    fontSize: '0.85rem',
    textTransform: 'uppercase',
  },
  '& tr:hover td': {
    background: 'rgba(55, 65, 81, 0.02)',
  },
});

const TableWrapper = styled('div')({
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
});

const InputSubsection = styled('div')({
  padding: '20px',
  margin: '16px 0',
  background: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  '@media (max-width: 768px)': {
    padding: '16px',
    margin: '12px 0',
  },
  '& h3': {
    marginTop: 0,
    paddingBottom: '12px',
    borderBottom: '1px solid var(--border-color)',
    marginBottom: '16px',
  },
});

const TwoCol = styled('div')<{ imageText?: boolean }>(({ imageText }) => ({
  display: 'grid',
  gridTemplateColumns: imageText ? 'auto 1fr' : '1fr 1fr',
  gap: '24px',
  margin: '16px 0',
  alignItems: imageText ? 'start' : 'stretch',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
}));

const SingleImage = styled('img')({
  maxWidth: '180px',
  borderRadius: '8px',
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '@media (max-width: 768px)': {
    maxWidth: '100%',
    marginBottom: '16px',
  },
});

const Comparison = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  margin: '16px 0',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
  '@media (max-width: 480px)': {
    gap: '12px',
  },
});

const ComparisonItem = styled('div')<{ variant: 'bad' | 'good' }>(
  ({ variant }) => ({
    padding: '20px',
    borderRadius: '8px',
    background: 'var(--code-bg)',
    border: `1px solid var(--border-color)`,
    borderLeftWidth: '4px',
    borderLeftColor:
      variant === 'bad' ? 'var(--text-muted)' : 'var(--primary-color)',
    '@media (max-width: 480px)': {
      padding: '16px',
    },
  }),
);

const ComparisonLabel = styled('div')<{ variant: 'bad' | 'good' }>(
  ({ variant }) => ({
    fontWeight: 600,
    marginBottom: '8px',
    color: variant === 'bad' ? 'var(--text-muted)' : 'var(--text-color)',
  }),
);

const Cheatsheet = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '16px',
  margin: '16px 0',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
  },
});

const CheatCard = styled('div')({
  background: 'var(--bg-color)',
  padding: '16px',
  borderRadius: '8px',
  borderLeft: '4px solid var(--primary-color)',
  '& code': {
    display: 'block',
    marginBottom: '8px',
  },
  '& small': {
    color: 'var(--text-muted)',
  },
});

const Workflow = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const WorkflowStep = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px',
  background: 'var(--bg-color)',
  borderRadius: '8px',
  '@media (max-width: 768px)': {
    padding: '12px',
    fontSize: '0.9rem',
  },
});

const WorkflowNumber = styled('div')({
  width: '32px',
  height: '32px',
  background: 'var(--primary-color)',
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  flexShrink: 0,
  '@media (max-width: 768px)': {
    width: '28px',
    height: '28px',
    fontSize: '0.9rem',
  },
});

const ThemeToggle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  fontSize: '0.8rem',
  color: 'var(--text-muted)',
});

const ThemeToggleSwitch = styled('span')<{ isDark: boolean }>(({ isDark }) => ({
  position: 'relative',
  width: '40px',
  height: '20px',
  background: isDark ? 'var(--accent-color)' : 'var(--border-color)',
  borderRadius: '10px',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '16px',
    height: '16px',
    background: 'var(--card-bg)',
    borderRadius: '50%',
    transform: isDark ? 'translateX(20px)' : 'translateX(0)',
    transition: 'transform 0.2s ease',
  },
}));

// Image Component
const ImageItem: React.FC<{
  src: string;
  alt: string;
  caption?: string;
  onClick: () => void;
}> = ({ src, alt, caption, onClick }) => (
  <ImageItemWrapper className="image-item" onClick={onClick}>
    <img src={src} alt={alt} />
    <ZoomHint>Click to enlarge</ZoomHint>
    {caption && <ImageCaption>{caption}</ImageCaption>}
  </ImageItemWrapper>
);

// Main Component
const Manual: React.FC = () => {
  const [sidebarActive, setSidebarActive] = useState(false);
  const [activeSection, setActiveSection] = useState('getting-started');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    images: [],
    currentIndex: 0,
  });

  // Image paths - update these to match your actual image locations
  const images = {
    step1: '/images/manual/Screenshot_20251213_061340_Haist.jpg',
    step2: '/images/manual/Screenshot_20251213_061346_Haist.jpg',
    step3: '/images/manual/Screenshot_20251213_061358_Haist.jpg',
    step4: '/images/manual/Screenshot_20251213_061420_Haist.jpg',
    photoInput: '/images/manual/Screenshot_20251213_061328_Haist.jpg',
    videoInput: '/images/manual/5b2b1706-cdaf-4204-81a1-ccdc3f551689.png',
    textInput: '/images/manual/f20cbe74-e388-424c-bab7-299e95a7071d.png',
    voiceMic: '/images/manual/fa2c19a0-b5f0-4fe8-b710-27e56f0f6fd8.png',
    voiceSpeaking: '/images/manual/6953caf6-06df-4495-a2f5-45776d6ea251.png',
    badPrompt: '/images/manual/Screenshot_20251213_113115_Haist.jpg',
    goodPrompt: '/images/manual/Screenshot_20251213_113024_Haist.jpg',
    estimateRequest: '/images/manual/Screenshot_20251213_114351_Haist.jpg',
    estimateResult: '/images/manual/Screenshot_20251213_114400_Haist.jpg',
    reportTag: '/images/manual/e6b7591f-abdf-4734-83c5-549521fa6138.png',
    reportType: '/images/manual/3a3929a6-6384-4371-99b2-48c632a846bd.png',
    reportResult: '/images/manual/3f58e75d-6f67-4ffd-a549-af637b6c409f.png',
  };

  // Theme handling
  useEffect(() => {
    const savedTheme = localStorage.getItem('manual-theme') as
      | 'light'
      | 'dark'
      | null;
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('manual-theme', newTheme);
  };

  // Modal handling
  const openModal = useCallback((imageList: ImageItem[], index: number) => {
    setModal({ isOpen: true, images: imageList, currentIndex: index });
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setModal((prev) => ({ ...prev, isOpen: false }));
    document.body.style.overflow = '';
  }, []);

  const navigateModal = useCallback((direction: number) => {
    setModal((prev) => ({
      ...prev,
      currentIndex:
        (prev.currentIndex + direction + prev.images.length) %
        prev.images.length,
    }));
  }, []);

  // Keyboard handling for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!modal.isOpen) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') navigateModal(-1);
      if (e.key === 'ArrowRight') navigateModal(1);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [modal.isOpen, closeModal, navigateModal]);

  // Cleanup body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = 'getting-started';
      sections.forEach((section) => {
        const sectionTop = (section as HTMLElement).offsetTop;
        if (window.scrollY >= sectionTop - 100) {
          current = section.getAttribute('id') || current;
        }
      });
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setSidebarActive(false);
    }
  };

  // Close sidebar on outside click
  useEffect(() => {
    if (!sidebarActive) return;
    const handleClick = (e: MouseEvent) => {
      const sidebar = document.getElementById('sidebar');
      const toggle = document.querySelector('.mobile-nav-toggle');
      if (
        sidebar &&
        !sidebar.contains(e.target as Node) &&
        toggle &&
        !toggle.contains(e.target as Node)
      ) {
        setSidebarActive(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [sidebarActive]);

  const navItems = [
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'input-methods', label: 'Input Methods' },
    { id: 'input-photo', label: 'Photo', isSub: true },
    { id: 'input-video', label: 'Video', isSub: true },
    { id: 'input-text', label: 'Text', isSub: true },
    { id: 'input-voice', label: 'Voice', isSub: true },
    { id: 'good-prompts', label: 'Good Prompts' },
    { id: 'more-info', label: 'More Info = Better', isSub: true },
    { id: 'what-to-include', label: 'What to Include', isSub: true },
    { id: 'diagnose', label: 'Diagnose' },
    { id: 'expert-routing', label: 'Expert Routing', isSub: true },
    { id: 'diagnostic-stages', label: 'Diagnostic Stages', isSub: true },
    { id: 'estimates', label: 'Estimates' },
    { id: 'get-estimate', label: 'Get an Estimate', isSub: true },
    { id: 'modify-estimates', label: 'Modify Estimates', isSub: true },
    { id: 'reports', label: 'Reports' },
    { id: 'how-to-generate', label: 'How to Generate', isSub: true },
    { id: 'report-types', label: 'Report Types', isSub: true },
  ];

  const refItems = [
    { id: 'quick-tips', label: 'Quick Tips' },
    { id: 'cheatsheet', label: 'Cheat Sheet' },
    { id: 'workflow', label: 'Workflow' },
  ];

  return (
    <ManualContainer data-theme={theme}>
      {globalStyles}

      {/* Mobile Header */}
      <MobileHeader>
        <MobileNavToggle
          className="mobile-nav-toggle"
          onClick={() => setSidebarActive(!sidebarActive)}
          aria-label="Toggle navigation"
        >
          <svg viewBox="0 0 24 24">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        </MobileNavToggle>
        <h1>HAIST Quick Guide</h1>
        <ThemeToggle onClick={toggleTheme}>
          <ThemeToggleSwitch isDark={theme === 'dark'} />
        </ThemeToggle>
      </MobileHeader>

      {/* Sidebar */}
      <Sidebar id="sidebar" isActive={sidebarActive}>
        <SidebarHeader>
          <h1>HAIST Quick Guide</h1>
        </SidebarHeader>
        <SidebarNav>
          <NavSection>Main Sections</NavSection>
          {navItems.map((item) => (
            <NavItem
              key={item.id}
              isActive={activeSection === item.id}
              isSub={item.isSub}
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </NavItem>
          ))}
          <NavSection>Reference</NavSection>
          {refItems.map((item) => (
            <NavItem
              key={item.id}
              isActive={activeSection === item.id}
              onClick={() => scrollToSection(item.id)}
            >
              {item.label}
            </NavItem>
          ))}
        </SidebarNav>
        <SidebarFooter>
          <ThemeToggle onClick={toggleTheme}>
            <ThemeToggleSwitch isDark={theme === 'dark'} />
            <span>Dark Mode</span>
          </ThemeToggle>
          <div style={{ marginTop: '8px' }}>v1.0 · Dec 2025</div>
        </SidebarFooter>
      </Sidebar>

      {/* Main Content */}
      <MainContent>
        {/* Getting Started */}
        <Section id="getting-started">
          <h2>Getting Started</h2>
          <p
            style={{
              color: 'var(--text-muted)',
              fontSize: '1.1rem',
              marginBottom: '20px',
            }}
          >
            Your AI-powered assistant for automotive diagnostics and repair.
          </p>

          <Callout variant="tip">
            <CalloutIcon>💡</CalloutIcon>
            <div>
              <strong>Always start with a license plate photo!</strong>
            </div>
          </Callout>

          <Flow>
            <FlowItem>Plate Photo</FlowItem>
            <FlowArrow>→</FlowArrow>
            <FlowItem>Vehicle Auto-Loaded</FlowItem>
            <FlowArrow>→</FlowArrow>
            <FlowItem>Start Chatting</FlowItem>
          </Flow>

          <h3>Step-by-Step Screenshots</h3>
          <ImageGallery>
            {[
              { src: images.step1, caption: '1. Open camera' },
              { src: images.step2, caption: '2. Take plate photo' },
              { src: images.step3, caption: '3. Vehicle loaded' },
              { src: images.step4, caption: '4. Start chatting' },
            ].map((img, idx, arr) => (
              <ImageItem
                key={idx}
                src={img.src}
                alt={img.caption}
                caption={img.caption}
                onClick={() =>
                  openModal(
                    arr.map((i) => ({ src: i.src, caption: i.caption })),
                    idx,
                  )
                }
              />
            ))}
          </ImageGallery>
        </Section>

        {/* Input Methods */}
        <Section id="input-methods">
          <h2>Input Methods</h2>

          {/* Photo */}
          <InputSubsection id="input-photo">
            <h3>Photo</h3>
            <TwoCol imageText>
              <SingleImage
                src={images.photoInput}
                alt="Photo input"
                onClick={() =>
                  openModal(
                    [{ src: images.photoInput, caption: 'Photo input' }],
                    0,
                  )
                }
              />
              <div>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>Use For</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>License plate</td>
                        <td>Start every job</td>
                      </tr>
                      <tr>
                        <td>Damaged parts</td>
                        <td>Worn brake pads, leaks</td>
                      </tr>
                      <tr>
                        <td>Scan tool screen</td>
                        <td>DTC codes</td>
                      </tr>
                    </tbody>
                  </Table>
                </TableWrapper>
                <Callout variant="tip" style={{ marginTop: '16px' }}>
                  <CalloutIcon>📷</CalloutIcon>
                  <div>
                    <strong>Tip:</strong> Keep photos steady and clear - blurry
                    images reduce accuracy. You can attach multiple photos at
                    once!
                  </div>
                </Callout>
              </div>
            </TwoCol>
          </InputSubsection>

          {/* Video */}
          <InputSubsection id="input-video">
            <h3>Video</h3>
            <TwoCol imageText>
              <SingleImage
                src={images.videoInput}
                alt="Video input"
                onClick={() =>
                  openModal(
                    [{ src: images.videoInput, caption: 'Video input' }],
                    0,
                  )
                }
              />
              <div>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>Use For</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Describe symptoms</td>
                        <td>Talk while recording</td>
                      </tr>
                      <tr>
                        <td>Customer complaints</td>
                        <td>Record their explanation</td>
                      </tr>
                    </tbody>
                  </Table>
                </TableWrapper>
                <Callout variant="tip" style={{ marginTop: '16px' }}>
                  <CalloutIcon>💡</CalloutIcon>
                  <div>
                    <strong>Tip:</strong> Speak while recording!
                  </div>
                </Callout>
                <p
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: '0.9rem',
                    marginTop: '8px',
                  }}
                >
                  Mechanical noise analysis coming soon
                </p>
              </div>
            </TwoCol>
          </InputSubsection>

          {/* Text */}
          <InputSubsection id="input-text">
            <h3>Text</h3>
            <TwoCol imageText>
              <SingleImage
                src={images.textInput}
                alt="Text input"
                onClick={() =>
                  openModal(
                    [{ src: images.textInput, caption: 'Text input' }],
                    0,
                  )
                }
              />
              <div>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>Use For</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Specs</td>
                        <td>
                          <Code>brake torque spec</Code>
                        </td>
                      </tr>
                      <tr>
                        <td>Procedures</td>
                        <td>
                          <Code>how to replace water pump</Code>
                        </td>
                      </tr>
                      <tr>
                        <td>DTC codes</td>
                        <td>
                          <Code>P0420 code what is it</Code>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </TableWrapper>
              </div>
            </TwoCol>
          </InputSubsection>

          {/* Voice */}
          <InputSubsection id="input-voice">
            <h3>Voice</h3>
            <TwoCol imageText>
              <div>
                <ImageGallery
                  style={{
                    gridTemplateColumns: '1fr 1fr',
                    maxWidth: '300px',
                  }}
                >
                  <ImageItem
                    src={images.voiceMic}
                    alt="Touch Mic"
                    caption="Touch Mic!"
                    onClick={() =>
                      openModal(
                        [
                          { src: images.voiceMic, caption: 'Touch Mic!' },
                          { src: images.voiceSpeaking, caption: 'Speaking!' },
                        ],
                        0,
                      )
                    }
                  />
                  <ImageItem
                    src={images.voiceSpeaking}
                    alt="Speaking"
                    caption="Speaking!"
                    onClick={() =>
                      openModal(
                        [
                          { src: images.voiceMic, caption: 'Touch Mic!' },
                          { src: images.voiceSpeaking, caption: 'Speaking!' },
                        ],
                        1,
                      )
                    }
                  />
                </ImageGallery>
              </div>
              <div>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <th>Use For</th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Hands dirty</td>
                        <td>Quick questions while working</td>
                      </tr>
                      <tr>
                        <td>Under the car</td>
                        <td>Hands-free input</td>
                      </tr>
                    </tbody>
                  </Table>
                </TableWrapper>
              </div>
            </TwoCol>
          </InputSubsection>
        </Section>

        {/* Good Prompts */}
        <Section id="good-prompts">
          <h2>Good Prompts</h2>

          <InputSubsection id="more-info">
            <h3>More Info = Better Answers</h3>
            <Comparison>
              <ComparisonItem variant="bad">
                <ComparisonLabel variant="bad">❌ Bad Example</ComparisonLabel>
                <Code>no start</Code>
                <ImageItemWrapper
                  style={{ marginTop: '16px' }}
                  onClick={() =>
                    openModal(
                      [{ src: images.badPrompt, caption: 'Bad prompt result' }],
                      0,
                    )
                  }
                >
                  <img src={images.badPrompt} alt="Bad prompt result" />
                  <ZoomHint>Click to enlarge</ZoomHint>
                </ImageItemWrapper>
              </ComparisonItem>
              <ComparisonItem variant="good">
                <ComparisonLabel variant="good">
                  ✅ Good Example
                </ComparisonLabel>
                <Code>
                  no start single click no crank battery 12.6v tried jump same
                  thing
                </Code>
                <ImageItemWrapper
                  style={{ marginTop: '16px' }}
                  onClick={() =>
                    openModal(
                      [
                        {
                          src: images.goodPrompt,
                          caption: 'Good prompt result',
                        },
                      ],
                      0,
                    )
                  }
                >
                  <img src={images.goodPrompt} alt="Good prompt result" />
                  <ZoomHint>Click to enlarge</ZoomHint>
                </ImageItemWrapper>
              </ComparisonItem>
            </Comparison>
          </InputSubsection>

          <InputSubsection id="what-to-include">
            <h3>What to Include</h3>
            <Table>
              <thead>
                <tr>
                  <th>Include</th>
                  <th>Example</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Symptom</td>
                  <td>clicking noise, vibration, leak</td>
                </tr>
                <tr>
                  <td>When it happens</td>
                  <td>cold start, at 60mph, always</td>
                </tr>
                <tr>
                  <td>What you checked</td>
                  <td>battery ok, fluid level good</td>
                </tr>
                <tr>
                  <td>Mileage</td>
                  <td>95000 miles</td>
                </tr>
                <tr>
                  <td>Driving conditions</td>
                  <td>coastal/salty, desert, hot/cold climate, mountains</td>
                </tr>
              </tbody>
            </Table>
            <Callout variant="success" style={{ marginTop: '16px' }}>
              <CalloutIcon>💡</CalloutIcon>
              <div>Vehicle info already loaded from plate photo!</div>
            </Callout>
          </InputSubsection>
        </Section>

        {/* Diagnose */}
        <Section id="diagnose">
          <h2>Diagnose</h2>

          <Callout variant="tip">
            <CalloutIcon>🤖</CalloutIcon>
            <div>
              The AI automatically routes your question to the appropriate
              specialist based on keywords.
            </div>
          </Callout>

          <Callout variant="success" style={{ marginTop: '16px' }}>
            <CalloutIcon>🔍</CalloutIcon>
            <div>
              AI organizes multiple possible causes and helps you quickly narrow
              down the root problem.
            </div>
          </Callout>

          <InputSubsection id="expert-routing">
            <h3>Automatic Expert Routing</h3>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>Expert</th>
                    <th>Specialty</th>
                    <th>Trigger Keywords</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Electrical</td>
                    <td>Electrical, sensors, battery</td>
                    <td>battery, alternator, sensor, wiring, no start</td>
                  </tr>
                  <tr>
                    <td>Powertrain</td>
                    <td>Engine, transmission, fuel</td>
                    <td>engine, transmission, misfire, timing</td>
                  </tr>
                  <tr>
                    <td>Braking</td>
                    <td>Brakes, tires, ABS</td>
                    <td>brake, pad, rotor, ABS, tire</td>
                  </tr>
                  <tr>
                    <td>Cooling</td>
                    <td>Cooling, A/C, heater</td>
                    <td>coolant, radiator, AC, overheat</td>
                  </tr>
                  <tr>
                    <td>Emission</td>
                    <td>Exhaust, catalyst, EGR</td>
                    <td>catalyst, O2 sensor, EGR, emission</td>
                  </tr>
                  <tr>
                    <td>Master</td>
                    <td>General/complex issues</td>
                    <td>All other questions</td>
                  </tr>
                </tbody>
              </Table>
            </TableWrapper>
          </InputSubsection>

          <InputSubsection id="diagnostic-stages">
            <h3>Diagnostic Stages</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: '12px' }}>
              Conversations progress through 3 stages:
            </p>
            <Flow>
              <FlowItem>
                <strong>1. Investigating</strong>
              </FlowItem>
              <FlowArrow>→</FlowArrow>
              <FlowItem>
                <strong>2. Confirmed</strong>
              </FlowItem>
              <FlowArrow>→</FlowArrow>
              <FlowItem>
                <strong>3. Repair Guide</strong>
              </FlowItem>
            </Flow>
            <Table style={{ marginTop: '16px' }}>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Investigating</strong>
                  </td>
                  <td>AI analyzes symptoms and asks for more info</td>
                </tr>
                <tr>
                  <td>
                    <strong>Confirmed</strong>
                  </td>
                  <td>Cause confirmed, provides diagnosis</td>
                </tr>
                <tr>
                  <td>
                    <strong>Repair Guide</strong>
                  </td>
                  <td>Step-by-step repair procedure</td>
                </tr>
              </tbody>
            </Table>
            <Callout variant="success" style={{ marginTop: '16px' }}>
              <CalloutIcon>💡</CalloutIcon>
              <div>
                Provide detailed context upfront to skip directly to confirmed
                diagnosis!
              </div>
            </Callout>
          </InputSubsection>

          <InputSubsection id="auto-search">
            <h3>Automatic Search</h3>
            <p>
              During diagnosis, the AI automatically searches external resources
              to provide comprehensive information:
            </p>
            <Table style={{ marginTop: '12px' }}>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>What It Finds</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Google Search</td>
                  <td>Technical bulletins, repair guides, common issues</td>
                </tr>
                <tr>
                  <td>YouTube Search</td>
                  <td>Video tutorials, step-by-step repair instructions</td>
                </tr>
              </tbody>
            </Table>
            <Callout variant="tip" style={{ marginTop: '16px' }}>
              <CalloutIcon>✨</CalloutIcon>
              <div>
                Search results are automatically integrated into responses.
              </div>
            </Callout>
          </InputSubsection>
        </Section>

        {/* Estimates */}
        <Section id="estimates">
          <h2>Estimates</h2>

          <Callout variant="warning">
            <CalloutIcon>⚠️</CalloutIcon>
            <div>
              <strong>About Parts Pricing:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>
                  If parts are found in search results → actual prices are added
                </li>
                <li>
                  If not found → approximate prices are shown (may not be
                  accurate)
                </li>
                <li>
                  <strong>Recommended:</strong> Adjust labor and parts prices
                  via prompts for accurate estimates
                </li>
              </ul>
            </div>
          </Callout>

          <InputSubsection id="get-estimate">
            <h3>Get an Estimate</h3>
            <p>
              <Code>front brake job estimate OEM parts</Code>
            </p>
            <ImageGallery
              style={{ gridTemplateColumns: '1fr 1fr', maxWidth: '500px' }}
            >
              <ImageItem
                src={images.estimateRequest}
                alt="Estimate request"
                caption="Request estimate"
                onClick={() =>
                  openModal(
                    [
                      {
                        src: images.estimateRequest,
                        caption: 'Request estimate',
                      },
                      {
                        src: images.estimateResult,
                        caption: 'Estimate result',
                      },
                    ],
                    0,
                  )
                }
              />
              <ImageItem
                src={images.estimateResult}
                alt="Estimate result"
                caption="Estimate result"
                onClick={() =>
                  openModal(
                    [
                      {
                        src: images.estimateRequest,
                        caption: 'Request estimate',
                      },
                      {
                        src: images.estimateResult,
                        caption: 'Estimate result',
                      },
                    ],
                    1,
                  )
                }
              />
            </ImageGallery>
          </InputSubsection>

          <InputSubsection id="modify-estimates">
            <h3>Modify Estimates</h3>

            <h4>Add Parts/Service</h4>
            <Table>
              <thead>
                <tr>
                  <th>Say This</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Code>add tire rotation</Code>
                  </td>
                  <td>Adds tire rotation to estimate</td>
                </tr>
                <tr>
                  <td>
                    <Code>also need rear brakes</Code>
                  </td>
                  <td>Adds rear brake service</td>
                </tr>
              </tbody>
            </Table>

            <h4>Change Parts/Price</h4>
            <Table>
              <thead>
                <tr>
                  <th>Say This</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Code>use aftermarket pads</Code>
                  </td>
                  <td>Swaps to aftermarket parts</td>
                </tr>
                <tr>
                  <td>
                    <Code>change labor to $150</Code>
                  </td>
                  <td>Updates labor price</td>
                </tr>
              </tbody>
            </Table>

            <h4>Remove Parts/Service</h4>
            <Table>
              <thead>
                <tr>
                  <th>Say This</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Code>remove rotor resurface</Code>
                  </td>
                  <td>Removes item from estimate</td>
                </tr>
                <tr>
                  <td>
                    <Code>skip the alignment</Code>
                  </td>
                  <td>Removes alignment service</td>
                </tr>
              </tbody>
            </Table>

            <h4>Pricing Features</h4>
            <Table>
              <thead>
                <tr>
                  <th>Say This</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Code>set target total to $800</Code>
                  </td>
                  <td>Adjusts prices to meet target total</td>
                </tr>
                <tr>
                  <td>
                    <Code>add 20% shop profit</Code>
                  </td>
                  <td>Auto-calculates and adds profit margin</td>
                </tr>
              </tbody>
            </Table>
          </InputSubsection>
        </Section>

        {/* Reports */}
        <Section id="reports">
          <h2>Reports</h2>

          <Callout variant="warning">
            <CalloutIcon>🚨</CalloutIcon>
            <div>
              <strong>Must select @report tag from menu!</strong>
            </div>
          </Callout>

          <InputSubsection id="how-to-generate">
            <h3>How to Generate Reports</h3>
            <ImageGallery
              style={{
                gridTemplateColumns: 'repeat(3, 1fr)',
                maxWidth: '600px',
              }}
            >
              <ImageItem
                src={images.reportTag}
                alt="Select report tag"
                caption="1. Select Report Tag"
                onClick={() =>
                  openModal(
                    [
                      {
                        src: images.reportTag,
                        caption: '1. Select Report Tag',
                      },
                      { src: images.reportType, caption: '2. Type message' },
                      {
                        src: images.reportResult,
                        caption: '3. Generated Report',
                      },
                    ],
                    0,
                  )
                }
              />
              <ImageItem
                src={images.reportType}
                alt="Type message"
                caption="2. Type message"
                onClick={() =>
                  openModal(
                    [
                      {
                        src: images.reportTag,
                        caption: '1. Select Report Tag',
                      },
                      { src: images.reportType, caption: '2. Type message' },
                      {
                        src: images.reportResult,
                        caption: '3. Generated Report',
                      },
                    ],
                    1,
                  )
                }
              />
              <ImageItem
                src={images.reportResult}
                alt="Report Result"
                caption="3. Generated Report"
                onClick={() =>
                  openModal(
                    [
                      {
                        src: images.reportTag,
                        caption: '1. Select Report Tag',
                      },
                      { src: images.reportType, caption: '2. Type message' },
                      {
                        src: images.reportResult,
                        caption: '3. Generated Report',
                      },
                    ],
                    2,
                  )
                }
              />
            </ImageGallery>
          </InputSubsection>

          <InputSubsection id="report-types">
            <h3>Report Types</h3>
            <Table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>When</th>
                  <th>How to Get</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <strong>Pre-Service</strong>
                  </td>
                  <td>Diagnosis only</td>
                  <td>
                    Select Report tag → <Code>give me a report</Code>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Service Completion</strong>
                  </td>
                  <td>Work done</td>
                  <td>
                    Select Report tag → <Code>job done give me a report</Code>
                  </td>
                </tr>
              </tbody>
            </Table>

            <Callout variant="tip" style={{ marginTop: '16px' }}>
              <CalloutIcon>💡</CalloutIcon>
              <div>
                Say <Code>job done</Code>, <Code>fixed it</Code>, or{' '}
                <Code>all done</Code> to generate a service completion report.
              </div>
            </Callout>
          </InputSubsection>

          <Callout
            variant="tip"
            id="before-after"
            style={{ marginTop: '16px' }}
          >
            <CalloutIcon>📸</CalloutIcon>
            <div>
              <strong>Before/After Photos</strong>
              <p style={{ marginTop: '4px' }}>
                Take photos during the conversation → They are automatically
                included in reports.
              </p>
            </div>
          </Callout>
        </Section>

        {/* Quick Tips */}
        <Section id="quick-tips">
          <h2>Quick Tips</h2>
          <ul style={{ paddingLeft: '20px' }}>
            <li>
              <strong>Start every session</strong> with a license plate photo
            </li>
            <li>
              <strong>Be specific</strong> in your descriptions
            </li>
            <li>
              <strong>Use voice</strong> when your hands are busy
            </li>
            <li>
              <strong>Take photos</strong> of damage for better diagnostics
            </li>
            <li>
              <strong>Review estimates</strong> before sharing with customers
            </li>
            <li>
              <strong>Document with before/after</strong> photos for reports
            </li>
          </ul>
        </Section>

        {/* Cheat Sheet */}
        <Section id="cheatsheet">
          <h2>Command Cheat Sheet</h2>

          <h3>Diagnose</h3>
          <Cheatsheet>
            <CheatCard>
              <Code>[symptom] + details</Code>
              <small>Start diagnosis</small>
            </CheatCard>
            <CheatCard>
              <Code>DTC photo</Code>
              <small>Read codes from scan tool</small>
            </CheatCard>
            <CheatCard>
              <Code>how to fix [issue]</Code>
              <small>Get repair guide</small>
            </CheatCard>
          </Cheatsheet>

          <h3>Estimates</h3>
          <Cheatsheet>
            <CheatCard>
              <Code>[service] estimate</Code>
              <small>Get estimate</small>
            </CheatCard>
            <CheatCard>
              <Code>add [service/part]</Code>
              <small>Add item</small>
            </CheatCard>
            <CheatCard>
              <Code>remove [item]</Code>
              <small>Remove item</small>
            </CheatCard>
            <CheatCard>
              <Code>change [item] to $XX</Code>
              <small>Update price</small>
            </CheatCard>
            <CheatCard>
              <Code>use OEM/aftermarket</Code>
              <small>Change parts type</small>
            </CheatCard>
          </Cheatsheet>

          <h3>Reports</h3>
          <Cheatsheet>
            <CheatCard>
              <Code>give me a report</Code>
              <small>Generate diagnostic report</small>
            </CheatCard>
            <CheatCard>
              <Code>job done</Code>
              <small>Generate service report</small>
            </CheatCard>
            <CheatCard>
              <Code>Insert photo</Code>
              <small>Photos auto-included in report</small>
            </CheatCard>
          </Cheatsheet>
        </Section>

        {/* Workflow */}
        <Section id="workflow">
          <h2>Workflow Summary</h2>
          <Workflow>
            <WorkflowStep>
              <WorkflowNumber>1</WorkflowNumber>
              <div>
                <strong>Scan plate</strong> - Vehicle info loads automatically
              </div>
            </WorkflowStep>
            <WorkflowStep>
              <WorkflowNumber>2</WorkflowNumber>
              <div>
                <strong>Diagnose</strong> - Describe symptoms, take photos of
                issues
              </div>
            </WorkflowStep>
            <WorkflowStep>
              <WorkflowNumber>3</WorkflowNumber>
              <div>
                <strong>Estimate</strong> - Request and customize repair
                estimate
              </div>
            </WorkflowStep>
            <WorkflowStep>
              <WorkflowNumber>4</WorkflowNumber>
              <div>
                <strong>Repair</strong> - Get specs and procedures as needed
              </div>
            </WorkflowStep>
            <WorkflowStep>
              <WorkflowNumber>5</WorkflowNumber>
              <div>
                <strong>Report</strong> - Generate service completion report
              </div>
            </WorkflowStep>
          </Workflow>
        </Section>
      </MainContent>

      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClick={(e) => e.target === e.currentTarget && closeModal()}
      >
        <ModalNav direction="prev" onClick={() => navigateModal(-1)}>
          ‹
        </ModalNav>
        <ModalContent>
          <ModalClose onClick={closeModal}>×</ModalClose>
          {modal.images[modal.currentIndex] && (
            <>
              <img
                src={modal.images[modal.currentIndex].src}
                alt={modal.images[modal.currentIndex].caption || ''}
              />
              {modal.images[modal.currentIndex].caption && (
                <ModalCaption>
                  {modal.images[modal.currentIndex].caption}
                </ModalCaption>
              )}
            </>
          )}
        </ModalContent>
        <ModalNav direction="next" onClick={() => navigateModal(1)}>
          ›
        </ModalNav>
      </Modal>
    </ManualContainer>
  );
};

export default Manual;
